import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { fetchEntries } from '../slices/timeEntrySlice';
import { fetchAllUsers } from '../slices/authSlice';
import Holidays from 'date-holidays';

const HolidayRow: React.FC<{ name: string; date: string; border?: boolean }> = ({ name, date, border }) => {
  const d = new Date(date);
  const day = d.getDate();
  const monthShort = d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');
  const full = d.toLocaleDateString('de-DE', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  return (
    <div
      className="d-flex align-items-center gap-3"
      style={{ padding: '10px 0', borderTop: border ? '1px solid var(--brand-border)' : 'none' }}
    >
      <div
        style={{
          width: 46, textAlign: 'center', background: 'var(--brand-orange-tint)',
          color: 'var(--brand-orange-dark)', borderRadius: 10, padding: '6px 0',
          lineHeight: 1.15, flex: '0 0 auto',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600 }}>{day}</div>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.03em' }}>{monthShort}</div>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{name}</div>
        <div style={{ fontSize: 12, color: '#8a857c', textTransform: 'capitalize' }}>{full}</div>
      </div>
    </div>
  );
};

const StartPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { entries } = useSelector((state: RootState) => state.time);
  const { allUsers } = useSelector((state: RootState) => state.auth);

  const [tick, setTick] = useState(0);
  const [upcomingHolidays, setUpcomingHolidays] = useState<
    { name: string; date: string }[]
  >([]);
  const [showAllHolidays, setShowAllHolidays] = useState(false);

  useEffect(() => {
    dispatch(fetchAllUsers());

    const today = new Date();
    const localDate = today.toLocaleDateString('sv-SE');

    dispatch(fetchEntries({ filter: 'day', date: localDate }));

    const hd = new Holidays('DE', 'BE');
    const year = new Date().getFullYear();
    const holidays = hd.getHolidays(year) || [];

    const now = new Date();
    const upcoming = holidays
      .filter((h) => new Date(h.date) >= now && h.type === 'public')
      .map((h) => ({
        name: h.name,
        date: h.date,
      }))
      .sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

    setUpcomingHolidays(upcoming);
  }, [dispatch]);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const today = new Date().toLocaleDateString('sv-SE');

      dispatch(
        fetchEntries({
          filter: 'day',
          date: today,
        })
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const getUserName = (id: string) => {
    const match = allUsers.find((u) => u._id === id);
    return match ? match.username : id;
  };

  const getEntriesForToday = (userId: string) => {
    const today = new Date().toLocaleDateString('sv-SE');
    return entries.filter(
      (e) =>
        e.userId === userId &&
        new Date(e.startTime).toLocaleDateString('sv-SE') === today
    );
  };

  const getLatestEntry = (userId: string) => {
    const userEntries = getEntriesForToday(userId);
    if (userEntries.length === 0) return null;
    return userEntries.reduce((latest, e) =>
      new Date(e.startTime) > new Date(latest.startTime) ? e : latest
    );
  };

  const formatDuration = (ms: number) => {
    if (ms < 0) ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const getStatus = (entry: any) => {
    if (!entry) return { text: 'Offline', color: 'red' };

    if (entry.type === 'vacation') {
      return { text: 'Urlaub', color: 'purple' };
    }

    if (entry.type === 'sick') {
      return { text: 'Krank', color: 'blue' };
    }

    if (!entry.endTime) {
      if (entry.isPaused) return { text: 'Pause', color: 'orange' };
      return { text: 'Arbeitet', color: 'green' };
    }

    return { text: 'Offline', color: 'red' };
  };

  const getCurrentDuration = (entry: any) => {
    if (!entry) return 0;

    if (entry.isPaused && entry.pauseStart) {
      const pauseStart = new Date(entry.pauseStart).getTime();
      let duration = pauseStart - new Date(entry.startTime).getTime();
      duration -= (entry.pausedDuration || 0) * 1000;
      return duration;
    }

    const end = entry.endTime ? new Date(entry.endTime).getTime() : Date.now();
    let duration = end - new Date(entry.startTime).getTime();
    duration -= (entry.pausedDuration || 0) * 1000;

    return duration;
  };

  const getFinishedDuration = (entry: any) => {
    if (!entry.startTime || !entry.endTime) return 0;

    const start = new Date(entry.startTime).getTime();
    const end = new Date(entry.endTime).getTime();

    let duration = end - start;
    duration -= (entry.pausedDuration || 0) * 1000;

    return duration;
  };

  const getTotalDurationToday = (userId: string) => {
    const todayEntries = getEntriesForToday(userId);

    return todayEntries
      .filter((e) => e.endTime)
      .reduce((total, e) => total + getCurrentDuration(e), 0);
  };

  return (
    <div className="container mt-4">
      <h2 className="text-left mb-4">Dashboard</h2>

      <div className="row g-4 align-items-start">
        <div className="col-lg-8">
          <div className="table-responsive">
            <table className="table table-bordered text-center mb-0">
        <thead>
          <tr>
            <th>Nutzer</th>
            <th>Status</th>
            <th>Aktueller Eintrag</th>
            <th>Gesamtzeit heute</th>
          </tr>
        </thead>
        <tbody>
          {allUsers.map((u) => {
            const latestEntry = getLatestEntry(u._id);
            const status = getStatus(latestEntry);
            const currentDuration = latestEntry
              ? getCurrentDuration(latestEntry)
              : 0;
            const totalDuration = getTotalDurationToday(u._id);

            return (
              <tr key={u._id}>
                <td>{getUserName(u._id)}</td>
                <td>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: status.color,
                      marginRight: '6px',
                    }}
                  ></span>
                  {status.text}
                </td>
                <td>{latestEntry ? formatDuration(currentDuration) : '-'}</td>
                <td>{formatDuration(totalDuration)}</td>
              </tr>
            );
          })}
        </tbody>
            </table>
          </div>
        </div>
        <div className="col-lg-4">
          <div className="card" style={{ position: 'sticky', top: 12 }}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="d-flex align-items-center gap-2" style={{ fontWeight: 600 }}>
            <i className="bi bi-calendar-event" style={{ color: 'var(--brand-orange)' }} aria-hidden="true"></i>
            Bevorstehende Feiertage
          </span>
          {upcomingHolidays.length > 3 && (
            <button
              className="btn btn-link btn-sm p-0"
              style={{ textDecoration: 'none', color: 'var(--brand-orange-dark)' }}
              onClick={() => setShowAllHolidays(true)}
            >
              Alle anzeigen ›
            </button>
          )}
        </div>

        {upcomingHolidays.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 14 }}>Keine anstehenden Feiertage.</div>
        ) : (
          upcomingHolidays.slice(0, 3).map((h, i) => (
            <HolidayRow key={i} name={h.name} date={h.date} border={i > 0} />
          ))
        )}
          </div>
        </div>
      </div>

      {showAllHolidays && (
        <div className="modal-overlay" onClick={() => setShowAllHolidays(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span style={{ fontWeight: 600 }}>Alle Feiertage {new Date().getFullYear()}</span>
              <button className="btn-close" aria-label="Schließen" onClick={() => setShowAllHolidays(false)} />
            </div>
            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {upcomingHolidays.map((h, i) => (
                <HolidayRow key={i} name={h.name} date={h.date} border={i > 0} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartPage;